import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/layout/SEOHead';
import { FadeIn } from '@/components/motion/FadeIn';
import { getAllProjects } from '@/lib/content';
import type { ProjectTypology } from '@/types/content';

interface ServiceSection {
  numeral: string;
  typology: ProjectTypology;
  title: string;
  subtitle: string;
  body: string[];
  hallmarks: string[];
}

const SERVICES: ServiceSection[] = [
  {
    numeral: 'I',
    typology: 'residential',
    title: 'Residential',
    subtitle: 'Private homes shaped by extended listening.',
    body: [
      'Private homes, second residences, and historic cottages. We work with a small number of families each year, and the work begins with extended listening — to how a family actually lives, what each room is asked to hold, and what the place itself wants to be.',
      'Each residential engagement is a year-long collaboration. We rebuild architecture and interior together; we source slowly; we sit with a material before we commit to it. The result is a home that doesn\'t look styled. It looks like the people who live in it.',
    ],
    hallmarks: [
      'Architecture + interior composed as one continuous gesture',
      'Honest materials, applied with restraint',
      'Furniture and millwork built to the site, not bought from a catalog',
      'Lighting designed for evening hours, not photo days',
    ],
  },
  {
    numeral: 'II',
    typology: 'hospitality',
    title: 'Hospitality',
    subtitle: 'Spaces composed for thousands of strangers.',
    body: [
      'Restaurants, boutique lodging, and small-format hospitality. The aesthetic posture is the same as residential — material restraint, considered light, hand-resolved details — but the brief is harder. Every surface gets touched a hundred times a week. Every detail has to survive the evening.',
      'We work closely with the chef-owner or operator from the first conversation. Service flow, sightlines, acoustic envelope, kitchen visibility — these aren\'t finish-out details, they\'re the room. The finish layer follows.',
    ],
    hallmarks: [
      'Operator-side fluency: kitchens, service flow, F&B economics',
      'Sound, light, and air engineered for the dinner hour',
      'Hospitality-grade materials that age well, not just look new',
      'Brand and interior aligned without becoming themed',
    ],
  },
  {
    numeral: 'III',
    typology: 'commercial',
    title: 'Commercial',
    subtitle: 'Workspaces that respect the work.',
    body: [
      'Showrooms, ateliers, design studios, and bespoke commercial interiors. Spaces composed for the trades who use them daily and the clients who arrive once and remember.',
      'Most commercial briefs ask for the wrong things — too much branding, too many surfaces, not enough thought to how the work actually flows. We bring the residential discipline (listen first, source slowly, simplify what we can) to spaces that have to perform for a business.',
    ],
    hallmarks: [
      'Workflow and circulation studied before finishes',
      'Lighting layered for tasks, ambient, and presentation',
      'Brand expression through material and proportion, not signage',
      'Built to be modified — businesses change, the interior should bend',
    ],
  },
];

export function ServicesPage() {
  const projects = getAllProjects();

  return (
    <>
      <SEOHead
        title="Services · Brian Ellison Design"
        description="Residential, hospitality, and commercial interior design by Brian Ellison Design — Melbourne, Florida. One studio, three kinds of room, one way of working."
        canonical="https://brianellisondesign.com/services"
        ogImage="/posters/og-projects.jpg"
      />
      <section className="page-hero">
        <div className="container">
          <FadeIn>
            <p className="eyebrow">Practice</p>
            <h1 className="page-hero__title">What we do.</h1>
            <p className="subtitle">
              Three kinds of room. One way of working. Each engagement is a year of close
              collaboration — not a moodboard, not a styling pass.
            </p>
          </FadeIn>
        </div>
      </section>

      {SERVICES.map((service, idx) => {
        const examples = projects.filter((p) => p.typology === service.typology).slice(0, 3);
        const flip = idx % 2 === 1;

        return (
          <section
            key={service.typology}
            className={`services-section ${flip ? 'services-section--flip' : ''}`}
          >
            <div className="container services-section__inner">
              <div className="services-section__lead">
                <FadeIn>
                  <p className="services-section__numeral display-italic">{service.numeral}</p>
                  <h2 className="services-section__title">{service.title}</h2>
                  <p className="services-section__subtitle display-italic">{service.subtitle}</p>
                </FadeIn>
              </div>

              <div className="services-section__body">
                <FadeIn delay={120}>
                  {service.body.map((p, i) => (
                    <p key={i} className="services-section__paragraph">{p}</p>
                  ))}
                </FadeIn>

                <FadeIn delay={240}>
                  <div className="services-section__hallmarks">
                    <p className="eyebrow">Hallmarks</p>
                    <ul className="services-section__hallmarks-list">
                      {service.hallmarks.map((h) => (
                        <li key={h} className="services-section__hallmark">
                          <span className="services-section__hallmark-mark" aria-hidden="true">·</span>
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeIn>

                {examples.length > 0 ? (
                  <FadeIn delay={320}>
                    <div className="services-section__examples">
                      <p className="eyebrow">Selected work</p>
                      <ul className="services-section__examples-list">
                        {examples.map((p) => (
                          <li key={p.slug}>
                            <Link to={`/projects/${p.slug}`} className="services-section__example-link">
                              <span className="display-italic">{p.title}</span>
                              <span className="caption services-section__example-meta">
                                {p.location} · {p.year}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <Link to={`/projects?type=${service.typology}`} className="text-link services-section__view-all">
                        View all {service.typology} →
                      </Link>
                    </div>
                  </FadeIn>
                ) : null}
              </div>
            </div>
          </section>
        );
      })}

      <p className="ornament container" aria-hidden="true">§</p>

      <section className="container section services-cta">
        <FadeIn>
          <p className="eyebrow">Get in touch</p>
          <h2 className="services-cta__heading display-italic">
            Considering a project?
          </h2>
          <p className="subtitle services-cta__body">
            We take a small number of engagements each year. Conversations start here.
          </p>
          <div className="services-cta__actions">
            <Link to="/contact" className="cta-ghost">Send an inquiry</Link>
            <Link to="/projects" className="text-link services-cta__secondary">View the work →</Link>
          </div>
        </FadeIn>
      </section>
    </>
  );
}
